import { useRef, Suspense, lazy, useEffect } from "react";
import { CLICKHOUSE_FUNCTIONS } from "../../data/clickhouseFunctions";
import { 
  getAllSchemas, 
  getTablesBySchema, 
  getAllTablesWithSchema, 
  getTableInfo 
} from "../../data/schemasAndTables";
import type { Dataset } from "../../types";

const MonacoEditor = lazy(() => import("@monaco-editor/react"));

interface MobileSQLInputProps {
  value: string;
  onChange: (v: string) => void;
  height?: string | number;
  datasets?: Dataset[];
  /** В чате (демо) нумерация строк отключена для экономии места */
  hideLineNumbers?: boolean;
}

export const MobileSQLInput = ({ value, onChange, height = "100%", datasets = [], hideLineNumbers = false }: MobileSQLInputProps) => {
  const monacoRef = useRef<any>(null);
  const completionDisposableRef = useRef<any>(null);
  const hoverDisposableRef = useRef<any>(null);
  const languageInitializedRef = useRef(false);
  const autofillObserverRef = useRef<MutationObserver | null>(null);

  const wrapperStyle: React.CSSProperties =
    typeof height === "number" ? { height: `${height}px` } : { height };

  // Обновляем провайдеры автодополнения/hover при смене datasets
  const registerProviders = (monaco: any) => {
    // Очищаем предыдущие провайдеры, чтобы избежать дубликатов
    completionDisposableRef.current?.dispose?.();
    hoverDisposableRef.current?.dispose?.();

    // Вычисляем uniqueFunctions здесь, чтобы гарантировать доступность
    const seenFunctions = new Set<string>();
    const uniqueFunctions = CLICKHOUSE_FUNCTIONS.filter(f => {
      const key = f.name.toLowerCase();
      if (seenFunctions.has(key)) return false;
      seenFunctions.add(key);
      return true;
    });

    // --- Автодополнение ---
    completionDisposableRef.current = monaco.languages.registerCompletionItemProvider("clickhouse-sql", {
      triggerCharacters: ['.'],
      provideCompletionItems: (model: any, position: any) => {
        const word = model.getWordUntilPosition(position);
        const textUntilPosition = model.getValueInRange({
          startLineNumber: 1,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        });

        const suggestions: any[] = [];
        
        // Set для отслеживания уже добавленных таблиц (чтобы избежать дубликатов)
        const addedTables = new Set<string>();

        // Анализируем контекст для умного автодополнения
        const textBeforeWord = textUntilPosition.substring(0, textUntilPosition.length - word.word.length);
        const lastWord = textBeforeWord.trim().split(/\s+/).pop() || "";
        
        // Определяем, где мы находимся в запросе
        const isAfterFrom = /FROM\s+$/i.test(textBeforeWord);
        const isAfterJoin = /\b(JOIN|LEFT JOIN|RIGHT JOIN|INNER JOIN|FULL JOIN|CROSS JOIN)\s+$/i.test(textBeforeWord);
        const isAfterSelect = /\bSELECT\s+$/i.test(textBeforeWord);
        const isAfterWhere = /\bWHERE\s+$/i.test(textBeforeWord);
        const isAfterOrderBy = /\bORDER\s+BY\s+$/i.test(textBeforeWord);
        const isAfterGroupBy = /\bGROUP\s+BY\s+$/i.test(textBeforeWord);
        const isAfterHaving = /\bHAVING\s+$/i.test(textBeforeWord);
        const isAfterOn = /\bON\s+$/i.test(textBeforeWord);
        const isAfterDot = /\.\s*$/.test(textBeforeWord);
        const isAfterSchemaDot = /(\w+)\.\s*$/.test(textBeforeWord);
        
        // Используем все схемы из полного списка
        const schemas = new Set<string>(getAllSchemas());

        // Подсказки для схем как отдельных сущностей (ПРИОРИТЕТ 0 - самый высокий)
        // Показываем схемы, когда пользователь начинает вводить имя (не после точки)
        if (!isAfterDot && !isAfterSchemaDot && schemas.size > 0) {
          // Если пользователь вводит текст, фильтруем схемы
          // Если текст пустой, показываем все схемы
          Array.from(schemas).forEach(schema => {
            if (word.word.length === 0 || schema.toLowerCase().startsWith(word.word.toLowerCase())) {
              suggestions.push({
                label: schema,
                kind: monaco.languages.CompletionItemKind.Module,
                insertText: schema,
                documentation: `Схема ${schema}`,
                sortText: `0_${schema}`,
              });
            }
          });
        }

        // Подсказки для схем (после точки или в начале идентификатора)
        if (isAfterSchemaDot || (isAfterDot && schemas.size > 0)) {
          const schemaMatch = textBeforeWord.match(/(\w+)\.\s*$/);
          if (schemaMatch) {
            const schemaName = schemaMatch[1];
            const schema = Array.from(schemas).find(s => s.toLowerCase() === schemaName.toLowerCase());
            if (schema) {
              // Показываем таблицы из этой схемы из полного списка (ПРИОРИТЕТ 1)
              getTablesBySchema(schema).forEach(tableName => {
                const tableKey = `${schema}.${tableName}`;
                if (!addedTables.has(tableKey)) {
                  addedTables.add(tableKey);
                  const tableInfo = getTableInfo(schema, tableName);
                  suggestions.push({
                    label: tableName,
                    kind: monaco.languages.CompletionItemKind.Class,
                    insertText: tableName,
                    documentation: tableInfo?.description || `Таблица ${tableName}`,
                    sortText: `1_${tableName}`,
                  });
                }
              });
            }
          }
        } else if (isAfterDot && !isAfterFrom && !isAfterJoin) {
          // После точки без схемы - показываем все таблицы из полного списка
          // (но только если не после FROM/JOIN, чтобы избежать дубликатов)
          const allTableNames = new Set<string>();
          getAllTablesWithSchema().forEach(fullTableName => {
            const tableName = fullTableName.split('.').pop() || '';
            if (!allTableNames.has(tableName) && !addedTables.has(fullTableName)) {
              allTableNames.add(tableName);
              addedTables.add(fullTableName);
              const [schema, name] = fullTableName.split('.');
              const tableInfo = getTableInfo(schema, name);
              suggestions.push({
                label: tableName,
                kind: monaco.languages.CompletionItemKind.Class,
                insertText: tableName,
                documentation: tableInfo?.description || `Таблица ${tableName}`,
                sortText: `1_${tableName}`,
              });
            }
          });
        }

        // Подсказки для таблиц (после FROM, JOIN, и т.д.) (ПРИОРИТЕТ 1)
        if (isAfterFrom || isAfterJoin) {
          // Добавляем таблицы из полного списка
          getAllTablesWithSchema().forEach(fullTableName => {
            if (!addedTables.has(fullTableName)) {
              addedTables.add(fullTableName);
              const [schema, name] = fullTableName.split('.');
              const tableInfo = getTableInfo(schema, name);
              suggestions.push({
                label: fullTableName,
                kind: monaco.languages.CompletionItemKind.Class,
                insertText: fullTableName,
                documentation: tableInfo?.description || `Таблица ${fullTableName}`,
                sortText: `1_${fullTableName}`,
              });
            }
          });
        }

        // Подсказки для колонок - в разных контекстах
        const columnContexts = isAfterSelect || isAfterWhere || isAfterOrderBy || 
                               isAfterGroupBy || isAfterHaving || isAfterOn || isAfterDot;
        
        if (columnContexts) {
          // Находим все таблицы, упомянутые в запросе
          const tableMatches = textUntilPosition.matchAll(/\bFROM\s+([\w.]+)|\bJOIN\s+([\w.]+)|\b(\w+)\.\w+/gi);
          const mentionedTables = new Set<string>();
          
          for (const match of tableMatches) {
            const tableRef = match[1] || match[2] || match[3];
            if (tableRef) {
              mentionedTables.add(tableRef);
            }
          }

          // Если есть упомянутые таблицы, показываем их колонки
          if (mentionedTables.size > 0) {
            mentionedTables.forEach(tableRef => {
              const tableNameOnly = tableRef.includes('.') 
                ? tableRef.split('.').pop() 
                : tableRef;
              
              const dataset = datasets.find(d => 
                d.name === tableNameOnly || 
                (d.schema && `${d.schema}.${d.name}` === tableRef)
              );

              if (dataset?.columns) {
                suggestions.push(
                  ...dataset.columns.map(col => ({
                    label: col.name,
                    kind: monaco.languages.CompletionItemKind.Field,
                    insertText: col.name,
                    documentation: col.description 
                      ? `${col.name}: ${col.type}\n${col.description}`
                      : `${col.name}: ${col.type}`,
                    sortText: `4_${col.name}`,
                  }))
                );
              }
            });
          } else {
            // Если таблицы не упомянуты, показываем колонки из всех таблиц
            datasets.forEach(dataset => {
              if (dataset.columns) {
                suggestions.push(
                  ...dataset.columns.map(col => ({
                    label: col.name,
                    kind: monaco.languages.CompletionItemKind.Field,
                    insertText: col.name,
                    documentation: dataset.name 
                      ? `${col.name}: ${col.type}\n${dataset.name}${col.description ? `\n${col.description}` : ''}`
                      : `${col.name}: ${col.type}${col.description ? `\n${col.description}` : ''}`,
                    sortText: `4_${col.name}`,
                  }))
                );
              }
            });
          }
        }

        // Если не в специальном контексте, все равно показываем таблицы
        // (но только если не было других подсказок для таблиц)
        const hasTableSuggestions = suggestions.some(s => s.kind === monaco.languages.CompletionItemKind.Class);
        if (!hasTableSuggestions && !isAfterFrom && !isAfterJoin && !isAfterDot) {
          // Добавляем таблицы из полного списка
          getAllTablesWithSchema().forEach(fullTableName => {
            if (!addedTables.has(fullTableName)) {
              addedTables.add(fullTableName);
              const [schema, name] = fullTableName.split('.');
              const tableInfo = getTableInfo(schema, name);
              suggestions.push({
                label: fullTableName,
                kind: monaco.languages.CompletionItemKind.Class,
                insertText: fullTableName,
                documentation: tableInfo?.description || `Таблица ${fullTableName}`,
                sortText: `1_${fullTableName}`,
              });
            }
          });
        }

        // Функции ClickHouse (ПРИОРИТЕТ 2)
        suggestions.push(
          ...uniqueFunctions.map(f => ({
            label: f.name,
            kind: monaco.languages.CompletionItemKind.Function,
            insertText: `${f.name}()`,
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: f.description,
            sortText: `2_${f.name}`,
          }))
        );

        // Ключевые слова (ПРИОРИТЕТ 3)
        const keywords = [
          "SELECT", "FROM", "WHERE", "GROUP", "BY", "ORDER", "LIMIT",
          "INSERT", "INTO", "VALUES", "AS", "JOIN", "ON", "ARRAY JOIN", "WITH",
          "SETTINGS", "FORMAT", "DESC", "ASC", "FINAL", "LEFT", "RIGHT", "OUTER",
          "INNER", "DISTINCT", "UNION", "ALL", "HAVING", "AND", "OR", "NOT", "GLOBAL IN",
          "GLOBAL JOIN", "LEFT JOIN", "RIGHT JOIN", "ANY JOIN", "ASOF JOIN"
        ];
        suggestions.push(
          ...keywords.map((k: string) => ({
            label: k,
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: k + " ",
            sortText: `3_${k}`,
          }))
        );

        // Для мобильной версии ограничиваем количество подсказок до 5
        const isMobile = window.innerWidth <= 768;
        const limitedSuggestions = isMobile ? suggestions.slice(0, 5) : suggestions;

        return { suggestions: limitedSuggestions };
      },
    });

    // --- Hover-подсказки ---
    hoverDisposableRef.current = monaco.languages.registerHoverProvider("clickhouse-sql", {
      provideHover: (model: any, position: any) => {
        const word = model.getWordAtPosition(position);
        if (!word) return null;
        
        const wordLower = word.word.toLowerCase();
        
        // Проверяем функции
        const func = uniqueFunctions.find(
          f => f.name.toLowerCase() === wordLower
        );
        
        if (func && func.description) {
          let description = func.description;
          if (description.length > 500) {
            description = description.substring(0, 500) + "...";
          }
          description = description.replace(/\n/g, "  \n");
          
          return {
            range: new monaco.Range(
              position.lineNumber,
              word.startColumn,
              position.lineNumber,
              word.endColumn
            ),
            contents: [
              { 
                value: `**${func.name}()**\n\n${description}`,
                isTrusted: true
              },
            ],
          };
        }
        
        // Проверяем таблицы из полного списка
        const allTablesWithSchema = getAllTablesWithSchema();
        for (const fullTableName of allTablesWithSchema) {
          const [schema, name] = fullTableName.split('.');
          if (fullTableName.toLowerCase() === wordLower || name.toLowerCase() === wordLower) {
            const tableInfo = getTableInfo(schema, name);
            let content = `**Таблица: ${fullTableName}**\n\n`;
            if (tableInfo?.description) {
              content += `${tableInfo.description}\n\n`;
            }
            
            // Если есть информация о колонках из datasets, добавляем её
            const dataset = datasets.find(d => 
              d.name === name && d.schema === schema
            );
            if (dataset?.columns && dataset.columns.length > 0) {
              content += `**Колонки:**\n`;
              dataset.columns.forEach(col => {
                content += `- \`${col.name}\`: ${col.type}`;
                if (col.description) {
                  content += ` - ${col.description}`;
                }
                content += `\n`;
              });
            }
            
            return {
              range: new monaco.Range(
                position.lineNumber,
                word.startColumn,
                position.lineNumber,
                word.endColumn
              ),
              contents: [
                { 
                  value: content,
                  isTrusted: true
                },
              ],
            };
          }
        }
        
        // Проверяем колонки
        for (const dataset of datasets) {
          if (dataset.columns) {
            const col = dataset.columns.find(c => c.name.toLowerCase() === wordLower);
            if (col) {
              let content = `**Колонка: ${col.name}**\n\n`;
              content += `**Тип:** ${col.type}\n\n`;
              if (dataset.name) {
                content += `**Таблица:** ${dataset.schema ? `${dataset.schema}.` : ''}${dataset.name}\n\n`;
              }
              if (col.description) {
                content += `${col.description}`;
              }
              
              return {
                range: new monaco.Range(
                  position.lineNumber,
                  word.startColumn,
                  position.lineNumber,
                  word.endColumn
                ),
                contents: [
                  { 
                    value: content,
                    isTrusted: true
                  },
                ],
              };
            }
          }
        }
        
        return null;
      },
    });
  };

  const handleEditorDidMount = (editor: any, monaco: any) => {
    monacoRef.current = monaco;

    if (!languageInitializedRef.current) {
      if (!monaco.languages.getLanguages().some((l: any) => l.id === "clickhouse-sql")) {
        monaco.languages.register({ id: "clickhouse-sql" });
      }

      const keywords = [
        "SELECT", "FROM", "WHERE", "GROUP", "BY", "ORDER", "LIMIT",
        "INSERT", "INTO", "VALUES", "AS", "JOIN", "ON", "ARRAY JOIN", "WITH",
        "SETTINGS", "FORMAT", "DESC", "ASC", "FINAL", "LEFT", "RIGHT", "OUTER",
        "INNER", "DISTINCT", "UNION", "ALL", "HAVING", "AND", "OR", "NOT", "GLOBAL IN",
        "GLOBAL JOIN", "LEFT JOIN", "RIGHT JOIN", "ANY JOIN", "ASOF JOIN"
      ];

      const seenFunctions = new Set<string>();
      const uniqueFunctions = CLICKHOUSE_FUNCTIONS.filter(f => {
        const key = f.name.toLowerCase();
        if (seenFunctions.has(key)) return false;
        seenFunctions.add(key);
        return true;
      });
      const functionNames = uniqueFunctions.map(f => f.name);

      // --- Monarch токенизация с игнорированием регистра ---
      monaco.languages.setMonarchTokensProvider("clickhouse-sql", {
        ignoreCase: true,
        defaultToken: "",
        keywords,
        functions: functionNames,
        tokenizer: {
          root: [
            [/\b\w+\b/, {
              cases: {
                "@keywords": "keyword",
                "@functions": "function",
                "@default": "identifier",
              }
            }],
            [/\d+/, "number"],
            [/".*?"/, "string"],
            [/'[^']*'/, "string"],
            [/--+.*/, "comment"],
          ],
        },
      });

      // --- Цветовая тема с новой палитрой ---
      monaco.editor.defineTheme("clickhouse-analyst-light", {
        base: "vs",
        inherit: true,
        rules: [
          { token: "keyword", foreground: "0abf53", fontStyle: "bold" }, // primary
          { token: "function", foreground: "067934" }, // accent-foreground
          { token: "identifier", foreground: "00112c" }, // foreground
          { token: "number", foreground: "0abf53" }, // primary
          { token: "string", foreground: "FF8C00" }, // оранжевый для строк
          { token: "comment", foreground: "5c687c", fontStyle: "italic", opacity: "0.6" }, // secondary-foreground
        ],
        colors: {
          "editor.background": "#FFFFFF",
          "editor.lineHighlightBackground": "#f7f7f8",
          "editorCursor.foreground": "#0abf53",
          "editorWhitespace.foreground": "#eeeff1",
          "editorLineNumber.foreground": "#5c687c",
          "editorLineNumber.activeForeground": "#0abf53",
        },
      });

      monaco.editor.setTheme("clickhouse-analyst-light");

      languageInitializedRef.current = true;
    }

    // Регистрируем провайдеры сразу с актуальными datasets
    registerProviders(monaco);

    // Отключаем автозаполнение Safari/Chrome на iOS
    const disableAutofill = () => {
      const container = editor.getContainerDomNode();
      if (!container) return;
      
      const textarea = container.querySelector('textarea');
      if (textarea) {
        textarea.setAttribute('autocomplete', 'off');
        textarea.setAttribute('autocorrect', 'off');
        textarea.setAttribute('autocapitalize', 'off');
        textarea.setAttribute('spellcheck', 'false');
        textarea.setAttribute('data-form-type', 'other');
        textarea.setAttribute('data-lpignore', 'true');
        textarea.setAttribute('data-1p-ignore', 'true');
        // Дополнительные атрибуты для Chrome на iOS
        textarea.setAttribute('inputmode', 'text');
        textarea.style.webkitAppearance = 'none';
        textarea.style.appearance = 'none';
      }
    };
    
    // Вызываем сразу и с задержкой на случай, если textarea создается асинхронно
    disableAutofill();
    setTimeout(disableAutofill, 100);
    setTimeout(disableAutofill, 500);
    
    // Также отслеживаем изменения DOM
    const container = editor.getContainerDomNode();
    if (container) {
      // Очищаем предыдущий observer, если есть
      if (autofillObserverRef.current) {
        autofillObserverRef.current.disconnect();
      }
      
      const observer = new MutationObserver(() => {
        disableAutofill();
      });
      
      autofillObserverRef.current = observer;
      observer.observe(container, {
        childList: true,
        subtree: true,
      });
    }
  };

  // Перерегистрируем провайдеры при смене datasets (чтобы подсказки по таблицам/схемам обновлялись)
  useEffect(() => {
    if (monacoRef.current) {
      registerProviders(monacoRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasets]);

  // Очистка observer при размонтировании
  useEffect(() => {
    return () => {
      if (autofillObserverRef.current) {
        autofillObserverRef.current.disconnect();
        autofillObserverRef.current = null;
      }
    };
  }, []);

  return (
    <div className="flex flex-col h-full bg-white border border-neutral-border rounded-xl" style={{ ...wrapperStyle, overflow: 'hidden', position: 'relative', maxHeight: '100%' }}>
      <Suspense fallback={
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-neutral-text/70 text-center font-body">Загрузка редактора...</div>
        </div>
      }>
        <MonacoEditor
          height="100%"
          language="clickhouse-sql"
          theme="clickhouse-analyst-light"
          value={value}
          onChange={(v) => onChange(v ?? "")}
          onMount={handleEditorDidMount}
          options={{
            minimap: { enabled: false },
            // ~на 15% компактнее базового мобильного тренажёра (было 11/13)
            fontSize: hideLineNumbers ? 10 : 11,
            wordWrap: "on",
            tabSize: 2,
            scrollBeyondLastLine: false,
            automaticLayout: true,
            padding: { top: 12, bottom: 12, left: 4, right: 4 },
            hover: {
              delay: 300,
              enabled: true,
            },
            // Оптимизации для мобильных устройств
            readOnly: false,
            contextmenu: true,
            quickSuggestions: {
              other: true,
              comments: false,
              strings: false,
            },
            suggestOnTriggerCharacters: true,
            acceptSuggestionOnEnter: "on",
            tabCompletion: "on",
            wordBasedSuggestions: "matchingDocuments",
            // Отключаем некоторые функции для производительности на мобильных
            renderWhitespace: "none",
            renderLineHighlight: "line",
            // Настройки для мобильной клавиатуры
            lineNumbers: hideLineNumbers ? "off" : "on",
            lineNumbersMinChars: hideLineNumbers ? 0 : 3,
            glyphMargin: false,
            // Улучшенная поддержка мобильных устройств
            scrollbar: {
              vertical: "auto",
              horizontal: "auto",
              useShadows: false,
            },
          }}
        />
      </Suspense>
    </div>
  );
};

